import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Menu, MenuItem, ListItemText, SvgIcon, ListItemIcon } from '@mui/material'
import StatementDrawer from 'components/customer-details/content/statements/StatementDrawer'
import { blobToBase64, getStatementBlob } from 'utils/file'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { ReactComponent as PdfIcon } from 'assets/PdfIcon.svg'
import { ReactComponent as ArrowSpin } from 'assets/ArrowSpin.svg'
import { Printer, DocumentText } from '@styled-icons/heroicons-outline'
import { get, lowerCase } from 'lodash'
import { useGenerateStatementMutation, useSendStatementMutation } from 'api/billing/statementCrud'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import { toast } from 'react-toastify'
import T from 'T'
import api from 'api'
import { CACHE_TAG_ACCOUNT_STATEMENT_LIST } from 'api/cacheTagTypes'
import { getTerm } from 'components/customer-details/content/statements/settings'
import { ReactComponent as PaperPlane } from 'assets/PaperPlane.svg'
import { handleError } from 'utils/error'

const StatementActionMenu = ({ anchorEl, statement, onClose, customerDetails, statementNumber }) => {
  const dispatch = useDispatch()
  const [isStatementDrawerOpen, setIsStatementDrawerOpen] = useState(false)
  const [isStatementRegenerateDialogOpen, setIsStatementRegenerateDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const accountId = get(customerDetails, 'id')
  const businessId = useSelector(state => state.AuthReducer.userInfo.businessInfo.businessId, shallowEqual)
  const startDate = get(statement, 'startDate', '').split('T')[0]
  const endDate = get(statement, 'endDate', '').split('T')[0]
  const [sendStatement] = useSendStatementMutation()
  const [generateStatementMutation] = useGenerateStatementMutation()
  const openStatementDrawer = () => {
    setIsStatementDrawerOpen(true)
  }

  const closeStatementDrawer = () => {
    setIsStatementDrawerOpen(false)
  }

  const openStatementRegenerateDialog = () => {
    setIsStatementRegenerateDialogOpen(true)
    onClose()
  }

  const closeStatementRegenerateDialog = () => {
    setIsStatementRegenerateDialogOpen(false)
  }

  const getStatement = callback => {
    setIsLoading(true)
    getStatementBlob(statementNumber, businessId)
      .then(blobToBase64)
      .then(base64 => {
        setIsLoading(false)
        callback(base64.split(',')[1])
      })
      .catch(error => {
        handleError(error)
        setIsLoading(false)
      })
  }

  const handleViewClick = () => {
    openStatementDrawer()
    onClose()
  }

  const handlePrintClick = () => {
    getStatement(pdfData => {
      const printableWindow = window.open('', '_blank')
      printableWindow.document.write(
        `<html><head><title>Print Statement</title></head><body style="margin:0">
          <embed src="data:application/pdf;base64,${pdfData}" width="100%" height="100%" type="application/pdf" />
        </body></html>`
      )
      printableWindow.document.close()
      printableWindow.focus()
    })
    onClose()
  }

  const handleDownloadClick = () => {
    getStatement(pdfData => {
      const link = document.createElement('a')
      link.href = `data:application/pdf;base64,${pdfData}`
      link.download = `${statementNumber || 'statement'}.pdf`
      link.click()
    })
    onClose()
  }

  const confirmStatementRegeneration = () => {
    const term = getTerm(lowerCase(statement.type))
    generateStatementMutation({ startDate, endDate, accountId, term })
      .unwrap()
      .then(() => {
        closeStatementRegenerateDialog()
        toast.success(T.STATEMENT_IS_REGENERATING, {
          hideProgressBar: false,
          autoClose: 4000,
          onClose: () => {
            dispatch(api.util.invalidateTags([CACHE_TAG_ACCOUNT_STATEMENT_LIST]))
          },
        })
      })
  }

  const handleSendStatement = async () => {
    const { id } = statement
    try {
      sendStatement({ statementIds: [id] })
      toast.success(T.SENDING_STATEMENT, {
        hideProgressBar: false,
        autoClose: 4000,
      })
    } catch (error) {
      handleError(error)
    } finally {
      onClose()
    }
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleViewClick} disabled={isLoading}>
          <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
            <SvgIcon>
              <DocumentText />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrintClick} disabled={isLoading}>
          <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
            <SvgIcon>
              <Printer />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadClick} disabled={isLoading}>
          <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
            <SvgIcon>
              <PdfIcon />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={openStatementRegenerateDialog} disabled={isLoading}>
          <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
            <SvgIcon>
              <ArrowSpin />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>Regenerate</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleSendStatement} disabled={isLoading}>
          <ListItemIcon sx={{ minWidth: 0, pr: 2 }}>
            <SvgIcon>
              <PaperPlane />
            </SvgIcon>
          </ListItemIcon>
          <ListItemText>Send</ListItemText>
        </MenuItem>
      </Menu>
      <HHConfirmDialog
        confirmTitle={T.STATEMENT_REGENERATION_TITLE}
        confirmDescription={T.STATEMENT_REGENERATION_DESCRIPTION}
        confirmButtonTitle={T.CONFIRM}
        ConfirmButtonProps={{ color: 'warning' }}
        onClose={closeStatementRegenerateDialog}
        isOpen={isStatementRegenerateDialogOpen}
        onConfirm={confirmStatementRegeneration}
      />
      <StatementDrawer
        open={isStatementDrawerOpen}
        onClose={closeStatementDrawer}
        statementNumber={statementNumber}
        customerDetails={customerDetails}
      />
    </>
  )
}

StatementActionMenu.propTypes = {
  anchorEl: PropTypes.object,
  onClose: PropTypes.func,
  customerDetails: PropTypes.object,
  statementNumber: PropTypes.object,
  statement: PropTypes.object,
}

export default StatementActionMenu
